import { Link, useNavigate } from '@tanstack/react-router';
import { PanelLeft, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Wordmark } from '~/components/brand/wordmark';
import { SidebarProjects } from '~/components/layout/sidebar-projects';
import { ThreadList } from '~/components/layout/thread-list';
import { ThreadSearchResults } from '~/components/layout/thread-search-results';
import { UserMenu } from '~/components/layout/user-menu';
import { Button } from '~/components/ui/button';
import { useAuthStatus } from '~/hooks/use-auth-status';
import { useCreateThread } from '~/hooks/use-threads';
import { useConfirmRemovedConversation } from '~/hooks/use-unused-conversation';
import { focusComposerSoon } from '~/lib/focus-after-navigation';
import { useT } from '~/lib/i18n';
import { ariaKeyShortcuts } from '~/lib/keyboard-shortcuts';
import { loopTab } from '~/lib/tab-loop';
import { cn } from '~/lib/utils';
import { useTemporaryChat } from '~/providers/temporary-chat-provider';

interface SidebarProps {
  open: boolean;
  mobile: boolean;
  onToggle: () => void;
}

export function Sidebar({ open, mobile, onToggle }: SidebarProps) {
  // Read here rather than threaded through the shell: the header needs the
  // logo and short name too, and the status query is already cached.
  const t = useT();
  const { data: status } = useAuthStatus();
  const branding = status?.branding;
  const [search, setSearch] = useState('');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const createThread = useCreateThread();
  const { setTemporary } = useTemporaryChat();
  // After a reload that removed an unused conversation, not listed (#266).
  useConfirmRemovedConversation();

  useEffect(() => {
    if (mobile && open) closeButtonRef.current?.focus();
  }, [mobile, open]);

  async function handleNewChat() {
    // New Chat is an ordinary chat, as the top bar's "+" and the shortcut are:
    // the button alone kept temporary mode, so work started here was deleted
    // after 24 hours (#340).
    setTemporary(false);
    await navigate({ to: '/' });
    if (mobile) onToggle();
    // After the drawer's own focus return, so the message box wins (#251).
    focusComposerSoon();
  }

  return (
    // The role switches with the responsive layout; aria-modal applies only to the mobile dialog.
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: dynamic role and aria-modal are kept in sync
    <aside
      role={mobile ? 'dialog' : undefined}
      aria-modal={mobile ? true : undefined}
      aria-label={mobile ? t('shell.sidebar.drawerLabel') : undefined}
      // What the shell looks the drawer up by: its name changes with the language.
      data-sidebar-drawer={mobile ? '' : undefined}
      aria-hidden={!open ? true : undefined}
      inert={!open ? true : undefined}
      // As a modal, Tab and Shift+Tab stay in it (#191).
      onKeyDown={mobile && open ? loopTab : undefined}
      className={cn(
        'fixed inset-y-0 start-0 z-50 flex h-dvh w-full shrink-0 flex-col bg-[var(--bg-app)] bg-[image:var(--sidebar-gradient)]',
        'transition-transform duration-200 ease-out lg:static lg:z-auto lg:w-64 lg:transition-[margin]',
        open
          ? 'translate-x-0 lg:ms-0'
          : '-translate-x-full rtl:translate-x-full lg:-ms-64 lg:translate-x-0',
      )}
    >
      <div className="flex h-14 items-center px-3">
        <Button
          ref={closeButtonRef}
          variant="ghost"
          size="icon-sm"
          onClick={onToggle}
          aria-label={t('shell.sidebar.close')}
          aria-keyshortcuts={ariaKeyShortcuts('toggle-sidebar')}
        >
          <PanelLeft className="rtl:-scale-x-100" />
        </Button>
        {/* Mark and name sit beside the toggle (v0.10): centring them needed a
            spacer that left too little room for the full default name. */}
        <Link to="/" className="flex min-w-0 flex-1 items-center px-2">
          <Wordmark
            name={branding?.appName}
            shortName={branding?.shortName}
            logoUrl={branding?.logoUrl}
            compact
          />
        </Link>
      </div>

      <div className="hidden px-3 pb-2 lg:block">
        <Button
          variant="primary"
          className="h-9 w-full font-semibold"
          onClick={handleNewChat}
          disabled={createThread.isPending}
          aria-keyshortcuts={ariaKeyShortcuts('new-chat')}
        >
          {t('shell.sidebar.newChat')}
        </Button>
      </div>

      <div className="relative px-3 py-1">
        <Search className="pointer-events-none absolute start-6 top-1/2 size-4 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && search) {
              event.stopPropagation();
              setSearch('');
            }
          }}
          placeholder={t('shell.sidebar.searchPlaceholder')}
          aria-label={t('shell.sidebar.searchLabel')}
          dir="auto"
          aria-describedby="thread-search-hint"
          // A field with an edge, like the others (1.4.11): it was a bare row above a rule.
          className="h-10 w-full rounded-lg border border-[var(--border-control)] bg-[var(--bg-control)] pe-3 ps-9 text-sm text-[var(--text-primary)] transition-colors placeholder:text-[var(--text-muted)] focus:border-[var(--text-muted)]"
        />
      </div>

      <p id="thread-search-hint" className="sr-only">
        {t('shell.sidebar.searchHint')}
      </p>

      <div className="mx-3 h-px bg-[var(--border-subtle)]" />

      <nav className="scrollbar-thin flex-1 overflow-y-auto px-3 py-3">
        {search.trim() ? (
          <ThreadSearchResults query={search} />
        ) : (
          <>
            <SidebarProjects />
            <ThreadList />
          </>
        )}
      </nav>

      <div className="hidden items-center gap-1 p-2 lg:flex">
        <div className="min-w-0 flex-1">
          <UserMenu />
        </div>
      </div>

      <div className="flex items-center justify-between px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] lg:hidden">
        <Button
          variant="accent"
          className="h-11 rounded-full px-5"
          onClick={handleNewChat}
          aria-keyshortcuts={ariaKeyShortcuts('new-chat')}
        >
          {t('shell.sidebar.newChat')}
        </Button>
        <UserMenu compact />
      </div>
    </aside>
  );
}
